import { Link } from 'react-router-dom'
import { usePageTitle } from '../lib/usePageTitle'

const CONSOLE_URL = 'https://app-bg.planesailing.io'

interface ProblemCard {
  label: string
  problem: string
  answer: string
}

const problems: ProblemCard[] = [
  {
    label: 'Key custody',
    problem: 'Key custody across a distributed org is a logistics nightmare.',
    answer:
      'Break Glass distributes Shamir shards to verified identities over the network. No safes, no couriers, no spreadsheet of who holds what — custody is tracked as ceremony state.',
  },
  {
    label: 'All-or-nothing',
    problem: 'Emergency access is all-or-nothing: either everyone can break in, or one person can.',
    answer:
      'A K-of-N quorum sets exactly how many people must cooperate. Tune the threshold to your risk: high enough that no individual acts alone, low enough that recovery is always possible.',
  },
  {
    label: 'Bus factor',
    problem: 'The secret lives with a single person — and bus-factor is one.',
    answer:
      'No single holder can unseal, and losing one holder doesn’t lock you out. Recovery survives departures, lost laptops, and people on a plane.',
  },
  {
    label: 'No audit trail',
    problem: 'There’s no tamper-evident record of who accessed what.',
    answer:
      'Every seal, possession proof, and unseal request is attributed to an OIDC-verified identity and recorded, giving you a tamper-evident audit of the whole ceremony.',
  },
  {
    label: 'Physical quorum',
    problem: 'Gathering all the keyholders in one room is impossible at remote scale.',
    answer:
      'Ceremonies are asynchronous. Each participant takes part from their own browser, on their own schedule — the protocol holds the state until a quorum is reached.',
  },
  {
    label: 'Vendor trust',
    problem: 'Using a managed vault means trusting a vendor with your plaintext.',
    answer:
      'No plaintext ever traverses a server — not even over TLS. The relay holds metadata and opaque ciphertext only and can never reconstruct your secret. You don’t have to trust it.',
  },
]

const quickComparisons: [string, string, string][] = [
  ['Custody', 'One person or a shared safe', 'K-of-N quorum of verified identities'],
  ['Recovery', 'All-or-nothing', 'Threshold you choose'],
  ['Bus factor', 'Single point of failure', 'Survives lost holders'],
  ['Audit', 'Out-of-band, if any', 'Tamper-evident, per-identity'],
  ['Trust model', 'Trust the vendor with plaintext', 'Server never sees plaintext'],
]

interface SecretType {
  label: string
  what: string
  use: string
  protect: string
}

const secretTypes: SecretType[] = [
  {
    label: 'Root CA / PKI key',
    what: 'The master key behind your private certificate authority.',
    use: 'It signs the internal certificates that let your services trust each other. Whoever holds it can mint a certificate for anything — impersonating any system on your network.',
    protect:
      'Seal it across a quorum so no single engineer can forge trust. The CA key surfaces only for the rare signing ceremony, with every use attributed and recorded.',
  },
  {
    label: 'AWS root account keys',
    what: 'The “god-mode” credentials for your cloud account.',
    use: 'They sit above every IAM policy and guardrail. Anyone holding them can change billing, delete everything, or lock you out — which is exactly why AWS tells you to lock them away.',
    protect:
      'Lock them behind a cross-team quorum. Pulling them out becomes a deliberate, witnessed ceremony — not a credential pasted in a wiki.',
  },
  {
    label: 'TOTP / 2FA seeds',
    what: 'The shared secret behind an authenticator’s six-digit codes.',
    use: 'For a critical shared account, that seed lets you generate valid 2FA codes forever. A screenshot of the QR in someone’s photo roll quietly defeats the whole second factor.',
    protect:
      'Custody the seed as a quorum instead of a screenshot. Recovery needs the group, and you keep a record of when the shared account’s second factor was ever reconstituted.',
  },
  {
    label: 'SSH master / bastion keys',
    what: 'The keys that open every server, or the CA that signs SSH access.',
    use: 'A master private key or SSH CA is a skeleton key to your fleet. One leaked copy and an attacker is on every host you own.',
    protect:
      'No single admin holds the master. Bringing it out to rotate or re-sign requires agreement from a quorum, so a single stolen laptop isn’t the whole kingdom.',
  },
  {
    label: 'Code-signing / GPG keys',
    what: 'The private key that signs your releases or encrypts your backups.',
    use: 'It proves software really came from you. Steal it and an attacker ships malware your customers’ machines will trust — a supply-chain nightmare.',
    protect:
      'Protect the private key itself with a people-quorum. It’s reconstituted only for a signing ceremony, then it’s gone again — never living whole on a build box.',
  },
]

export function ProblemsPage() {
  usePageTitle('Problems it solves')

  return (
    <div className="page">
      <section className="page-hero narrow reveal">
        <div className="hero-pill emergency">Problems it solves</div>
        <h1>Emergency access shouldn&apos;t mean trusting one person or one vendor.</h1>
        <p className="hero-lead">
          Break-glass access is where most secret-management stories quietly fall apart: custody is
          ad-hoc, recovery is all-or-nothing, and the audit trail is a memory. Here&apos;s what Break
          Glass fixes, one problem to one answer.
        </p>
      </section>

      <section className="content-section">
        <div className="section-heading reveal">
          <div className="eyebrow">One problem, one answer</div>
          <h2>The break-glass problems, solved by construction.</h2>
          <p className="muted">
            Each of these is a structural property of the ceremony — not a setting you have to
            remember to switch on.
          </p>
        </div>
        <div className="problem-grid">
          {problems.map((p) => (
            <article className="problem-card reveal" key={p.label}>
              <div className="panel-label">{p.label}</div>
              <h3>{p.problem}</h3>
              <div className="answer">
                <div className="answer-label">Break Glass</div>
                {p.answer}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section">
        <div className="section-heading reveal">
          <div className="eyebrow">What it&apos;s for</div>
          <h2>The keys that, in the wrong hands, are game over.</h2>
          <p className="muted">
            Break Glass is built for the small set of secrets so powerful they should never sit with
            one person — the ones you reach for once a year, in an emergency, and want a group to
            unlock together.
          </p>
        </div>
        <div className="problem-grid">
          {secretTypes.map((s) => (
            <article className="problem-card reveal" key={s.label}>
              <div className="panel-label">{s.label}</div>
              <h3>{s.what}</h3>
              <p className="muted">{s.use}</p>
              <div className="answer">
                <div className="answer-label">With Break Glass</div>
                {s.protect}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section content-band">
        <div className="section-heading reveal">
          <div className="eyebrow">Quick scan</div>
          <h2>What changes when emergency access becomes a ceremony?</h2>
        </div>
        <div className="mini-table reveal">
          {quickComparisons.map(([label, left, right]) => (
            <div className="mini-table-row" key={label}>
              <strong>{label}</strong>
              <span>{left}</span>
              <span>{right}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="content-section">
        <div className="section-heading reveal">
          <div className="eyebrow emergency">Why not just GPG?</div>
          <h2>GPG gives you a quorum of one. Break Glass gives you a quorum of people.</h2>
          <p className="muted">
            You can absolutely just use GPG — and for plenty of jobs you should. But GPG encrypts the
            secret to <em>every</em> recipient&apos;s public key. That&apos;s a quorum of one: any
            single person in the group can decrypt the secret entirely on their own. Break Glass goes
            a step further and requires a quorum of people to come together — in patterns you define.
          </p>
        </div>
        <div className="problem-grid">
          <article className="problem-card reveal">
            <div className="panel-label">Simple threshold</div>
            <h3>3 of 5 must be present.</h3>
            <div className="answer">
              <div className="answer-label">Pattern</div>
              Split a secret across five holders and require any three to take part before it can be
              reconstructed. No single person — and no pair — can act alone, yet you can still recover
              if two holders are unavailable.
            </div>
          </article>
          <article className="problem-card reveal">
            <div className="panel-label">Structured quorum</div>
            <h3>Two per team, across teams.</h3>
            <div className="answer">
              <div className="answer-label">Pattern</div>
              Require, say, two people each from management, security, and operations. Now six
              engineers together still can&apos;t unseal the secret alone — reconstruction needs
              cross-functional agreement, not just a headcount.
            </div>
          </article>
        </div>
        <p className="muted center-note reveal">
          GPG protects a secret <em>at rest</em>. Break Glass governs <em>who must agree</em> to bring
          it back — and proves, cryptographically, that they did.
        </p>
      </section>

      <section className="content-section">
        <div className="signup-cta reveal">
          <div>
            <div className="eyebrow">Next step</div>
            <h2>See the feature set or open the console.</h2>
            <p className="muted">
              If the bus-factor and vendor-trust pain is real for your team, the only question is
              whether you self-host or use the hosted service.
            </p>
          </div>
          <div className="cta-actions">
            <Link className="button button-solid" to="/features">
              See features
            </Link>
            <a className="button button-ghost" href={CONSOLE_URL}>
              Launch console
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
